import { InlineIcon } from '@iconify/react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { MutableRefObject, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import useStep from 'src/hooks/useStep';
import useWindowSize from 'src/hooks/useWindowSize';
import useAutoFocus from './useAutoFocus';

const GUIDE_ME_DIALOG_MAX_WIDTH_PX = 500;
const GUIDE_ME_MOBILE_LAYOUT_MAX_WIDTH_PX = 425;
const GUIDE_ME_DIALOG_VIEWPORT_GUTTER_PX = 24;

interface Step {
  content: ReactNode;
  title: string | React.ReactElement<any>;
}

interface GuideMeProps {
  steps: Step[];
  guideId: string;
  focusRef?: MutableRefObject<any>;
}

const useGuideMe = ({ steps, guideId, focusRef }: GuideMeProps) => {
  const [
    step,
    { canGoToNextStep, canGoToPrevStep, goToNextStep, goToPrevStep, reset, isLastStep },
  ] = useStep(steps.length);
  const [open, setOpen] = useState(false);
  const { width: viewportWidth } = useWindowSize();
  const dialogWidth = useMemo(() => {
    if (viewportWidth <= 0) {
      return GUIDE_ME_DIALOG_MAX_WIDTH_PX;
    }

    return Math.min(
      GUIDE_ME_DIALOG_MAX_WIDTH_PX,
      viewportWidth - GUIDE_ME_DIALOG_VIEWPORT_GUTTER_PX
    );
  }, [viewportWidth]);
  const showMobileCloseButton =
    viewportWidth > 0 && viewportWidth <= GUIDE_ME_MOBILE_LAYOUT_MAX_WIDTH_PX;
  const buttonRef = useAutoFocus(open, [
    { canGoToNextStep, canGoToPrevStep, goToNextStep, goToPrevStep, reset, isLastStep },
  ]);

  const handleOpenGuideMe = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseGuideMe = useCallback(() => {
    setOpen(false);
    reset();
    if (focusRef?.current) {
      buttonRef.current?.blur();
      focusRef?.current.focus();
    }
  }, [buttonRef, focusRef, reset]);

  const currentStep = useMemo(() => steps[step - 1], [step, steps]);

  const { t } = useTranslation();
  const guideMe = useMemo(
    () => (
      <>
        <Button
          onClick={handleOpenGuideMe}
          endIcon={<InlineIcon icon="material-symbols:play-circle" />}
          color="secondary"
        >
          {t('Guide Me')}
        </Button>
        <Dialog
          onClose={handleCloseGuideMe}
          open={open && !!steps.length}
          width={dialogWidth}
          className="guide-me-dialog"
          title={currentStep?.title}
          id={guideId}
        >
          <div className="px-2 max-h-[65vh]">
            {currentStep?.content}
            {isLastStep && (
              <div className={`flex items-center gap-1 mt-2 font-semibold text-[13px]`}>
                <div>{t('Click the')}</div>
                <Button
                  endIcon={<InlineIcon icon="material-symbols:play-circle" height={14} width={14} />}
                  color="secondary"
                  readonly={true}
                  size="md"
                  className="inline-block"
                >
                  {t('Guide Me')}
                </Button>
                <div className="font-semibold"> {t('button to show this again.')}</div>
              </div>
            )}
          </div>
          <DialogActionsBar>
            <div className="flex items-center justify-end">
              <div className="flex items-center gap-1">
                {canGoToPrevStep && (
                  <Button color="info" onClick={goToPrevStep} onKeyDown={goToPrevStep}>
                    {t('Back')}
                  </Button>
                )}
                {canGoToNextStep && (
                  <Button
                    color="info"
                    onClick={goToNextStep}
                    onKeyDown={goToNextStep}
                    ref={buttonRef}
                  >
                    {t('Next')}
                  </Button>
                )}
                {(isLastStep || showMobileCloseButton) && (
                  <Button
                    ref={isLastStep ? buttonRef : undefined}
                    color="info"
                    onClick={handleCloseGuideMe}
                    onKeyDown={handleCloseGuideMe}
                  >
                    {t('Close')}
                  </Button>
                )}
              </div>
            </div>
          </DialogActionsBar>
        </Dialog>
      </>
    ),
    [
      buttonRef,
      canGoToNextStep,
      canGoToPrevStep,
      currentStep?.content,
      currentStep?.title,
      dialogWidth,
      goToNextStep,
      goToPrevStep,
      guideId,
      handleCloseGuideMe,
      handleOpenGuideMe,
      isLastStep,
      open,
      showMobileCloseButton,
      steps.length,
      t,
    ]
  );

  return {
    guideMe,
    openGuideMe: handleOpenGuideMe,
  };
};

export default useGuideMe;
